import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import {
  ApplicationAccessSnapshot,
  ApplicationUserAccess,
  RemoveApplicationUserAccessRequest,
  SetApplicationUserAccessRequest,
  UserApplicationAccessResponse,
} from '@core/interfaces/application-access';
import { environment } from '@env';
import { Observable, tap } from 'rxjs';

/**
 * Client for the flags-router application-access API (Layer B per-user grants + the caller's own
 * effective allow set). Backend entitlement (Layer A) is managed from backoffice via tenant flags.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationAccessService {
  private readonly http = inject(HttpClient);

  private readonly entryPoint = `${environment.FLAGS_ROUTER_SERVICE_URL}/api/v1/application-access`;

  // Fed to <ol-header [applicationAccess]> so the switcher enables only the apps the caller can reach.
  private readonly accessState = signal<ApplicationAccessSnapshot>({
    loaded: false,
    allowedApps: [],
  });
  readonly applicationAccess = this.accessState.asReadonly();

  loadMyAccess(): Observable<UserApplicationAccessResponse> {
    return this.http
      .get<UserApplicationAccessResponse>(`${this.entryPoint}/me`)
      .pipe(
        tap((response) =>
          this.accessState.set({ loaded: true, allowedApps: response.allowedApps ?? [] }),
        ),
      );
  }

  listByApp(appId: string): Observable<ApplicationUserAccess[]> {
    return this.http.get<ApplicationUserAccess[]>(`${this.entryPoint}/${appId}/users`);
  }

  setUserAccess(body: SetApplicationUserAccessRequest): Observable<ApplicationUserAccess> {
    return this.http.post<ApplicationUserAccess>(this.entryPoint, body);
  }

  removeUserAccess(body: RemoveApplicationUserAccessRequest): Observable<void> {
    return this.http.delete<void>(this.entryPoint, { body });
  }
}
